import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { nanoid } from 'nanoid';
import 'twin.macro';
import useNotification from 'containers/App/Notifications/useNotification';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { useUpdateCJEventAttachment } from 'containers/CahierJournal/queries/useUploadCJEventAttachment';
import Disclosure from 'components/Disclosure';
import { BoWithLocalFallbackCycle } from 'components/Bo/BoWithLocalFallbackCycle';
import emptyBo from 'components/Bo/emptyBo';
import Editor from 'components/Editor/formWrapper';
import Button from 'components/Buttons/Button';
import {
  BriefcaseIcon,
  PaperClipIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ButtonAttachment from 'components/Attachments/ButtonAttachment';
import { LinkedFichesDePrep } from 'containers/CahierJournal/EventModal/ModalBody/Classic/LinkedFichesDePreps';
import { AttachmentComponent as Attachments } from 'components/Attachments/Attachment';
import { CJAtelier } from 'types/cj';
import MaterialItems from 'components/Materials/Items';
import { EditorAIWrapperFF } from 'components/Editor/EditorAIWrapper';
import { useAIAssistCahierJournal } from 'containers/CahierJournal/hooks/useAIAssistCahierJournal';
import { useGetAIAssistCJAtelierContextFromForm } from 'containers/CahierJournal/hooks/useGetAIAssistCJAtelierContextFromForm';
import { Attachment } from 'types/attachment';
import { useField } from 'react-final-form';

interface AtelierPanelProps {
  atelier: CJAtelier;
  onChange: (atelier: CJAtelier) => void;
}

export function AtelierDisclosurePanel(props: AtelierPanelProps) {
  const { atelier, onChange } = props;
  const aiContext = useGetAIAssistCJAtelierContextFromForm(atelier);
  const aiHook = useAIAssistCahierJournal({ context: aiContext });
  const { t } = useTranslation(['common', 'cahierJournal']);
  const { notifyError, notifySuccess } = useNotification();
  const user = useUser();
  const isBoEmpty =
    atelier.bo.matiere.length === 0 &&
    atelier.bo.domaine.length === 0 &&
    atelier.bo.competence.length === 0 &&
    atelier.bo.goals.length === 0;

  const attachmentsField = useField<Attachment[]>('attachments');
  const { isUploading, upload } = useUpdateCJEventAttachment();

  async function handleAddAttachment(file: File) {
    const attachment = await upload({ file });
    if (attachment) {
      const currentAttachments = attachmentsField.input.value || [];
      attachmentsField.input.onChange([...currentAttachments, attachment]);
      notifySuccess({
        title: t('common:messages.attachmentAddedSuccess'),
      });
    }
  }

  const [isExpandedReferentiel, setExpandReferentiel] = useState(!isBoEmpty);
  return (
    <Disclosure.Panel>
      <div tw="flex flex-col gap-y-4 bg-white px-4 pt-2 flex-grow">
        <div tw="flex flex-col flex-wrap gap-y-1">
          {!isExpandedReferentiel && (
            <div>
              <Button
                onClick={() => setExpandReferentiel(v => !v)}
                icon={PlusIcon}
                minimal
                fill
                alignText="left"
              >
                {t('cahierJournal:ateliers.modal.referentielItems')}
              </Button>
            </div>
          )}

          {isExpandedReferentiel && (
            <div tw="flex gap-x-2">
              <div>
                <Button
                  onClick={() => setExpandReferentiel(v => !v)}
                  icon={XMarkIcon}
                  minimal
                  fill
                  alignText="left"
                />
              </div>
              <div tw="flex-grow">
                <BoWithLocalFallbackCycle
                  hideHeader
                  cycle={atelier.cycle}
                  boId={user.referencielId}
                  values={
                    atelier.bo
                      ? { ...atelier.bo, cycle: atelier.cycle }
                      : { ...emptyBo, cycle: atelier.cycle }
                  }
                  onChange={v =>
                    onChange({ ...atelier, bo: v, cycle: v.cycle })
                  }
                />
              </div>
            </div>
          )}
          {(atelier.materials || []).length > 0 ? (
            <div>
              <MaterialItems
                onChange={v => {
                  notifySuccess({
                    title: t('common:components.materials.materialSavedToList'),
                  });
                  onChange({
                    ...atelier,
                    materials: v,
                  });
                }}
                items={atelier.materials || []}
                enableEdit={!user.guestMode?.enabled}
              />
            </div>
          ) : (
            <Button
              onClick={() =>
                onChange({
                  ...atelier,
                  materials: [
                    {
                      id: nanoid(8),
                      value: '',
                    },
                  ],
                })
              }
              icon={BriefcaseIcon}
              minimal
              fill
              alignText="left"
            >
              {t('common:components.materials.addMaterial')}
            </Button>
          )}
        </div>

        <div>
          <EditorAIWrapperFF
            value={atelier.flow}
            onChange={v => onChange({ ...atelier, flow: v })}
            aiHook={aiHook}
            trackingContext="CahierJournal"
            onPdfAddAttachment={handleAddAttachment}
          >
            <Editor
              placeholder={t('cahierJournal:ateliers.modal.flowPlaceholder')}
              onChange={v => onChange({ ...atelier, flow: v })}
              value={atelier.flow}
            />
          </EditorAIWrapperFF>
        </div>
        <div tw="flex flex-col gap-y-2">
          <ButtonAttachment
            onChange={v =>
              onChange({
                ...atelier,
                attachments: v,
              })
            }
            attachments={atelier.attachments || []}
            fill
            alignText="left"
            label={t('common:customFields.actions.addAFile')}
            icon={PaperClipIcon}
            uploadingAttachment={isUploading}
            handleError={({ message }) => {
              notifyError({ title: message });
            }}
            multiple
            minimal
            handleUploadAttachment={({ file }) =>
              upload({ file })
                .then(v => v)
                .catch(() => {
                  notifyError({
                    title: t('common:messages.problemUploadingFile'),
                  });
                })
            }
          />
        </div>
        <div>
          <LinkedFichesDePrep
            allowAddAnother={!user.guestMode?.enabled}
            linkedFichesDePrep={atelier.linkedFichesDePrep}
            onChange={v => {
              onChange({
                ...atelier,
                linkedFichesDePrep: v,
              });
            }}
          />
          <Attachments
            attachments={atelier.attachments}
            onChange={v => {
              onChange({
                ...atelier,
                attachments: v,
              });
            }}
          />
        </div>
      </div>
    </Disclosure.Panel>
  );
}
